import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ConfigService } from '@orion-platform/core';
import { environment } from '@orion/environment';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../../../../../../../../../libs/core/src/lib/services/notification.service';
import { PnVcFlags } from '../models/supply-visibility.model';
import {
  CreatePnVcFlags,
  PnVc,
  VirtualPartNumberDetail,
  VirtualPartNumberDetailResponse,
  VirtualPartNumberGroup,
  VirtualPartNumberResponse,
  VirtualPNDetail,
  VirtualPNDetailPayload,
} from '../models/virtual-pn-groups.model';

@Injectable({
  providedIn: 'root',
})
export class VirtualPnGroupsService {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);
  private notificationService = inject(NotificationService);
  private apiUrl!: string;

  private isFlagLoaded = new Subject<boolean>();
  public VIRTUAL_VENDOR_CODE = 'VirtualVC';

  constructor() {
    this.apiUrl = this.configService.getSettings('apiBaseUrl') as string;
  }

  getFlagOperation(): Observable<boolean> {
    return this.isFlagLoaded.asObservable();
  }

  setFlagOperation(isFlagLoaded: boolean): void {
    this.isFlagLoaded.next(isFlagLoaded);
  }

  getEndpoint(action: string): string {
    return `${this.apiUrl}${
      environment.modulesBaseUrl.materialManagement.virtualPartNumbers
    }${'/'}${action}`;
  }

  getPartNumbers(action: string): string {
    return `${this.apiUrl}${
      environment.modulesBaseUrl.materialManagement.partNumbers
    }${'/'}${action}`;
  }

  getVirtualPartNumbers(payload: ApiQueryParams): Observable<VirtualPartNumberResponse> {
    let params = new HttpParams();
    if (payload.skip !== null && payload.skip !== undefined) {
      params = params.append('$skip', payload.skip);
    }
    if (payload.top !== null && payload.top !== undefined) {
      params = params.append('$top', payload.top);
    }
    if (payload.sort !== null && payload.sort !== undefined) {
      params = params.append('$orderby', payload.sort as string);
    }
    if (payload.searchValue) {
      params = params.append('searchText', payload.searchValue);
    }

    return this.http.get<VirtualPartNumberResponse>(
      `${this.getEndpoint('GetVirtualPartNumbers')}`,
      {
        params: params,
      },
    );
  }

  getVirtualPartNumbersDetails(
    id: string,
    skip?: number,
    top?: number,
  ): Observable<VirtualPartNumberDetailResponse> {
    let params = new HttpParams();
    params = params.append('VirtualPartNumberID', id);

    if (skip !== null && skip !== undefined) {
      params = params.append('$skip', skip);
    }

    if (top !== null && top !== undefined) {
      params = params.append('$top', top);
    }

    return this.http.get<VirtualPartNumberDetailResponse>(
      `${this.getEndpoint('GetVirtualPartNumberDetails')}`,
      {
        params: params,
      },
    );
  }

  insertVirtualPartNumber(partNumber: VirtualPartNumberGroup): Observable<VirtualPartNumberDetail> {
    partNumber.virtualVendorCode = this.VIRTUAL_VENDOR_CODE;
    partNumber.virtualPartnumber = partNumber.virtualPartnumber.trim();
    //Gets Error When Duplicate GroupNumber
    //Send "duplicate" in case title: "Validation exception", status: 400, detail: "Already exists VirtualPartnumbers combination"
    return this.http.post<VirtualPartNumberDetail>(
      `${this.getEndpoint('InsertVirtualPartNumber')}`,
      partNumber,
      {},
    );
  }

  updateVirtualPartNumber(partNumber: VirtualPartNumberGroup): Observable<VirtualPartNumberDetail> {
    partNumber.virtualVendorCode = this.VIRTUAL_VENDOR_CODE;
    partNumber.virtualPartnumber = partNumber.virtualPartnumber.trim();
    return this.http.post<VirtualPartNumberDetail>(
      `${this.getEndpoint('UpdateVirtualPartNumber')}`,
      partNumber,
      {},
    );
  }

  deleteVirtualPartNumber(partNumber: VirtualPartNumberDetail): Observable<unknown> {
    const params = new HttpParams();
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http
      .delete(`${this.getEndpoint('DeleteVirtualPartNumber')}/${partNumber.id}`, {
        headers,
        params,
        responseType: 'text',
      })
      .pipe(catchError(this.handleError));
  }

  insertVirtualPartNumberDetail(
    partNumber: VirtualPNDetailPayload,
  ): Observable<unknown | VirtualPNDetail> {
    if (!partNumber.virtualPartNumberId) {
      this.notificationService.showError('PartNumberGroup Id missing!');
      return throwError((): Error => new Error('PartNumberGroup Id missing!'));
    }
    return this.http
      .post(`${this.getEndpoint('InsertVirtualPartNumberDetail')}`, partNumber, {})
      .pipe(catchError(this.handleError));
  }

  updateVirtualPartNumberDetail(
    partNumber: VirtualPNDetailPayload,
  ): Observable<unknown | VirtualPNDetail> {
    if (!partNumber.virtualPartNumberId) {
      this.notificationService.showError('PartNumberGroup Id missing!');
      return throwError((): Error => new Error('PartNumberGroup Id missing!'));
    }
    if (!partNumber.id) {
      this.notificationService.showError('PartNumber Id missing!');
      return throwError((): Error => new Error('PartNumber Id missing!'));
    }

    return this.http
      .post(`${this.getEndpoint('UpdateVirtualPartNumberDetail')}`, partNumber, {})
      .pipe(catchError(this.handleError));
  }

  deleteVirtualPartNumberDetail(partNumber: VirtualPNDetailPayload): Observable<unknown> {
    const params = new HttpParams();
    const headers = new HttpHeaders().set('Content-Type', 'text/plain; charset=utf-8');
    return this.http
      .delete(`${this.getEndpoint('DeleteVirtualPartNumberDetail')}/${partNumber.id}`, {
        headers,
        params,
        responseType: 'text',
      })
      .pipe(catchError(this.handleError));
  }

  getPartNumberVendorCodeFlags(PNGroup: PnVc[]): Observable<PnVcFlags[]> {
    return this.http
      .post<PnVcFlags[]>(`${this.getPartNumbers('GetPartNumberVendorCodeFlags')}`, PNGroup)
      .pipe(catchError((err): Observable<never> => this.handleError(err) as Observable<never>));
  }

  createPartNumberVendorCodeFlags(PNFlags: CreatePnVcFlags): Observable<PnVcFlags | unknown> {
    return this.http.post<PnVcFlags>(
      `${this.getPartNumbers('CreatePartNumberVendorCodeFlag')}`,
      PNFlags,
    );
  }

  deletePartNumberVendorCodeFlags(PNFlags: CreatePnVcFlags): Observable<PnVcFlags | unknown> {
    PNFlags.partNumber = encodeURIComponent(PNFlags.partNumber);
    PNFlags.vendorCode = encodeURIComponent(PNFlags.vendorCode);
    PNFlags.flag = encodeURIComponent(PNFlags.flag);

    return this.http.delete(
      `${this.getPartNumbers('DeletePartNumberVendorCodeFlag')}/${PNFlags.partNumber}/${PNFlags.vendorCode}/${PNFlags.flag}`,
    );
  }

  private handleError(error: HttpErrorResponse): Observable<unknown> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error.detail}`);
    }
    // Return an observable with a user-facing error message.
    return throwError('Something bad happened; please try again later.');
  }

  getPartNumbersListForSV(virtualPn: string): Promise<VirtualPNDetail[]> {
    let payload = {
      searchValue: virtualPn,
      skip: 0,
      top: 10,
    };
    return new Promise((resolve): void => {
      this.getVirtualPartNumbers(payload).subscribe((res): void => {
        if (res && res.result) {
          for (let i = 0; i < res.result.length; i++) {
            if (res.result[i].virtualPartnumber.trim() === virtualPn.trim()) {
              this.getVirtualPartNumbersDetails(res.result[i].id, 0, 1000).subscribe(
                (list): void => {
                  if (list && list?.result && list.result.length > 0) {
                    resolve(list?.result);
                  }
                },
              );
            }
          }
        }
      });
    });
  }
}
